import type { SupabaseClient } from "@supabase/supabase-js";

import {
  LOCAL_AUTH_MAIL_BUDGET_MESSAGE,
  sendMemberAuthInviteEmail,
  type MemberAuthInviteSendOutcome,
} from "./member-auth-invite";
import {
  inviteAccountMember,
  type InviteRole,
  type MemberInviteRow,
} from "./member-invites";
import type { MemberRow } from "./members";
import { normalizeInviteEmailQuery } from "./platform-people-search";

/** Product lock D-02: at most 25 normalized unique addresses per batch. */
export const BULK_INVITE_MAX_ROWS = 25;

export const BULK_INVITE_OVER_CAP_SENTENCE =
  "This batch is limited to 25 addresses. Remove rows before sending.";

export const BULK_INVITE_GUARD_NAME_REQUIRED_SENTENCE =
  "Bulk invite for Guard needs a display name for each person. Use the single invite form, or choose Supervisor or Admin for this batch.";

export const BULK_INVITE_REFUSAL_SENTENCE = {
  already_member: "Active account membership already exists for this email.",
  pending_exists: "Pending invite already exists for this email.",
  invalid_address: "Invite email is not valid.",
  role_not_supported: "Invite role is not supported.",
  guard_name_required: BULK_INVITE_GUARD_NAME_REQUIRED_SENTENCE,
  auth_mail_budget_untouched:
    "The sign in mail budget for this hour is used. These addresses were not queued. Their invite reservations were not created. Try them again later.",
  auth_mail_budget_reserved: "Reserved; sign-in mail not queued.",
  known_conflict_window_note:
    "Known from the loaded member and invite window only. The host still validates each send.",
} as const;

/** Full-domain mask for bulk ledger/preview (D-17). Existing Platform lists keep domain-retaining redact. */
export function maskInviteAddressFullDomain(email: string): string {
  const value = email.trim();
  const separator = value.indexOf("@");
  if (separator <= 0 || separator === value.length - 1) return "•••";
  const local = value.slice(0, separator);
  const domain = value.slice(separator + 1);
  const localMask = `${local.slice(0, 1)}${"•".repeat(Math.max(6, local.length - 1))}`;
  const domainMask = domain.replace(/[^.]+/g, (part) => "•".repeat(Math.max(part.length, 1)));
  return `${localMask}@${domainMask}`;
}

export type BulkInvitePreviewFlag =
  | "invalid_address"
  | "already_member"
  | "pending_exists"
  | "guard_name_required"
  | "eligible";

export type BulkInviteAuthOutcome =
  | "invite_email_sent"
  | "already_registered"
  | "not_attempted"
  | "mail_refused"
  | "mail_budget"
  | "unknown";

export type BulkInviteRowOutcome =
  | {
      status: "reserved";
      authOutcome: BulkInviteAuthOutcome;
      inviteId: string | null;
      sentence: string | null;
    }
  | {
      status: "refused";
      reason:
        | "already_member"
        | "pending_exists"
        | "invalid_address"
        | "role_not_supported"
        | "guard_name_required";
      sentence: string;
    }
  | {
      status: "not_queued";
      reason: "auth_mail_budget";
      sentence: string;
    }
  | {
      status: "unavailable";
      reason: "unavailable" | "ambiguous";
      sentence: string;
    }
  | {
      status: "skipped";
      reason: "cancelled" | "stopped_after_budget" | "not_attempted";
      sentence: string;
    };

export interface BulkInvitePreviewRow {
  /** Normalized address for matching and shipped RPC only. Never render raw. */
  readonly normalizedEmail: string;
  readonly maskedAddress: string;
  readonly displayName: string | null;
  readonly duplicateCount: number;
  readonly previewFlag: BulkInvitePreviewFlag;
  readonly previewSentence: string | null;
  readonly eligible: boolean;
}

export interface BulkInviteKnownWindows {
  readonly members: readonly MemberRow[];
  readonly invites: readonly MemberInviteRow[];
}

function normalizeSeparators(raw: string): string[] {
  return raw
    .split(/[\s,;]+/u)
    .map((part) => part.trim())
    .filter((part) => part.length > 0);
}

function knownMemberEmails(members: readonly MemberRow[]): Set<string> {
  const set = new Set<string>();
  for (const member of members) {
    if (member.status !== "active" && member.status !== "suspended") continue;
    const email = member.memberEmail?.trim().toLowerCase() ?? "";
    if (email.length > 0) set.add(email);
  }
  return set;
}

function knownPendingInviteEmails(invites: readonly MemberInviteRow[]): Set<string> {
  const set = new Set<string>();
  for (const invite of invites) {
    if (invite.status !== "pending") continue;
    const email = invite.email.trim().toLowerCase();
    if (email.length > 0) set.add(email);
  }
  return set;
}

/** Parse paste into a capped preview. Does not write. */
export function buildBulkInvitePreview(input: {
  rawPaste: string;
  role: InviteRole;
  windows: BulkInviteKnownWindows;
}): {
  rows: readonly BulkInvitePreviewRow[];
  overCap: boolean;
  tokenCount: number;
  uniqueCount: number;
  eligibleCount: number;
  guardRoleBlocked: boolean;
} {
  const tokens = normalizeSeparators(input.rawPaste);
  const overCap = tokens.length > BULK_INVITE_MAX_ROWS;
  const cappedTokens = tokens.slice(0, BULK_INVITE_MAX_ROWS);

  const counts = new Map<string, { raw: string; count: number }>();
  for (const token of cappedTokens) {
    const normalized = normalizeInviteEmailQuery(token) ?? token.trim().toLowerCase();
    const existing = counts.get(normalized);
    if (existing) existing.count += 1;
    else counts.set(normalized, { raw: token, count: 1 });
  }

  const memberEmails = knownMemberEmails(input.windows.members);
  const pendingEmails = knownPendingInviteEmails(input.windows.invites);
  const guardRoleBlocked = input.role === "guard";

  const rows: BulkInvitePreviewRow[] = [];
  for (const [normalized, { count }] of counts) {
    const valid = normalizeInviteEmailQuery(normalized) !== null;
    let previewFlag: BulkInvitePreviewFlag = "eligible";
    let previewSentence: string | null = null;
    let eligible = true;

    if (!valid) {
      previewFlag = "invalid_address";
      previewSentence = BULK_INVITE_REFUSAL_SENTENCE.invalid_address;
      eligible = false;
    } else if (memberEmails.has(normalized)) {
      previewFlag = "already_member";
      previewSentence = `${BULK_INVITE_REFUSAL_SENTENCE.already_member} ${BULK_INVITE_REFUSAL_SENTENCE.known_conflict_window_note}`;
      eligible = false;
    } else if (pendingEmails.has(normalized)) {
      previewFlag = "pending_exists";
      previewSentence = `${BULK_INVITE_REFUSAL_SENTENCE.pending_exists} ${BULK_INVITE_REFUSAL_SENTENCE.known_conflict_window_note}`;
      eligible = false;
    } else if (guardRoleBlocked) {
      previewFlag = "guard_name_required";
      previewSentence = BULK_INVITE_REFUSAL_SENTENCE.guard_name_required;
      eligible = false;
    }

    rows.push({
      normalizedEmail: normalized,
      maskedAddress: maskInviteAddressFullDomain(normalized),
      displayName: null,
      duplicateCount: count,
      previewFlag,
      previewSentence,
      eligible,
    });
  }

  const eligibleCount = rows.filter((row) => row.eligible).length;
  return {
    rows,
    overCap,
    tokenCount: tokens.length,
    uniqueCount: rows.length,
    eligibleCount,
    guardRoleBlocked,
  };
}

function mapInviteRefusal(reason: string): BulkInviteRowOutcome {
  if (reason === BULK_INVITE_REFUSAL_SENTENCE.already_member) {
    return {
      status: "refused",
      reason: "already_member",
      sentence: BULK_INVITE_REFUSAL_SENTENCE.already_member,
    };
  }
  if (reason === BULK_INVITE_REFUSAL_SENTENCE.pending_exists) {
    return {
      status: "refused",
      reason: "pending_exists",
      sentence: BULK_INVITE_REFUSAL_SENTENCE.pending_exists,
    };
  }
  if (reason === BULK_INVITE_REFUSAL_SENTENCE.invalid_address) {
    return {
      status: "refused",
      reason: "invalid_address",
      sentence: BULK_INVITE_REFUSAL_SENTENCE.invalid_address,
    };
  }
  if (reason === BULK_INVITE_REFUSAL_SENTENCE.role_not_supported) {
    return {
      status: "refused",
      reason: "role_not_supported",
      sentence: BULK_INVITE_REFUSAL_SENTENCE.role_not_supported,
    };
  }
  return {
    status: "unavailable",
    reason: "unavailable",
    sentence: "Couldn't complete that invite change - please try again.",
  };
}

function mapMailOutcome(
  mail: MemberAuthInviteSendOutcome,
): Pick<Extract<BulkInviteRowOutcome, { status: "reserved" }>, "authOutcome" | "sentence"> {
  if (mail.ok) {
    return { authOutcome: mail.kind, sentence: null };
  }
  if (mail.failureKind === "local_auth_mail_budget") {
    return {
      authOutcome: "mail_budget",
      sentence: BULK_INVITE_REFUSAL_SENTENCE.auth_mail_budget_reserved,
    };
  }
  return {
    authOutcome: "mail_refused",
    sentence: mail.reason,
  };
}

export interface BulkInviteSendRowResult {
  readonly normalizedEmail: string;
  readonly maskedAddress: string;
  readonly duplicateCount: number;
  readonly outcome: BulkInviteRowOutcome;
}

export interface BulkInviteSendOptions {
  readonly client: SupabaseClient;
  readonly accountId: string;
  readonly role: InviteRole;
  readonly rows: readonly BulkInvitePreviewRow[];
  readonly redirectTo: string;
  readonly generation: number;
  readonly isCurrent: () => boolean;
  readonly shouldCancel: () => boolean;
  readonly onRowSettled?: (result: BulkInviteSendRowResult, index: number) => void;
}

/**
 * Sequential invite_account_member then member-auth-invite per eligible row.
 * Stops scheduling new creates after local Auth mail budget or ambiguous create.
 * Guard batches never create: host display_name is optional, so the adapter enforces G-INV-02.
 */
export async function sendBulkInviteBatch(
  options: BulkInviteSendOptions,
): Promise<{
  readonly generation: number;
  readonly results: readonly BulkInviteSendRowResult[];
  readonly stoppedReason: "done" | "cancelled" | "auth_mail_budget" | "ambiguous" | "stale";
}> {
  const results: BulkInviteSendRowResult[] = [];
  let stopNewCreates: "auth_mail_budget" | "ambiguous" | "cancelled" | "stale" | null = null;

  // Defense in depth: bulk never ships Guard without per-row names (preview + send must agree).
  if (options.role === "guard") {
    for (let index = 0; index < options.rows.length; index += 1) {
      const row = options.rows[index]!;
      const refused: BulkInviteSendRowResult = {
        normalizedEmail: row.normalizedEmail,
        maskedAddress: row.maskedAddress,
        duplicateCount: row.duplicateCount,
        outcome: {
          status: "refused",
          reason: "guard_name_required",
          sentence: BULK_INVITE_REFUSAL_SENTENCE.guard_name_required,
        },
      };
      results.push(refused);
      options.onRowSettled?.(refused, index);
    }
    return { generation: options.generation, results, stoppedReason: "done" };
  }

  for (let index = 0; index < options.rows.length; index += 1) {
    const row = options.rows[index]!;
    if (!options.isCurrent()) {
      stopNewCreates = "stale";
      for (let rest = index; rest < options.rows.length; rest += 1) {
        const leftover = options.rows[rest]!;
        const skipped: BulkInviteSendRowResult = {
          normalizedEmail: leftover.normalizedEmail,
          maskedAddress: leftover.maskedAddress,
          duplicateCount: leftover.duplicateCount,
          outcome: {
            status: "skipped",
            reason: "cancelled",
            sentence: "Send stopped because the workspace or session changed.",
          },
        };
        results.push(skipped);
        options.onRowSettled?.(skipped, rest);
      }
      return { generation: options.generation, results, stoppedReason: "stale" };
    }

    if (stopNewCreates !== null || options.shouldCancel()) {
      const reason = options.shouldCancel() ? "cancelled" : stopNewCreates ?? "cancelled";
      const outcome: BulkInviteRowOutcome =
        reason === "auth_mail_budget"
          ? {
              status: "not_queued",
              reason: "auth_mail_budget",
              sentence: BULK_INVITE_REFUSAL_SENTENCE.auth_mail_budget_untouched,
            }
          : {
              status: "skipped",
              reason: reason === "ambiguous" ? "not_attempted" : "cancelled",
              sentence:
                reason === "ambiguous"
                  ? "Not attempted after an ambiguous earlier row. Reconcile before retrying."
                  : "Cancelled. Completed reservations were kept.",
            };
      const skipped: BulkInviteSendRowResult = {
        normalizedEmail: row.normalizedEmail,
        maskedAddress: row.maskedAddress,
        duplicateCount: row.duplicateCount,
        outcome,
      };
      results.push(skipped);
      options.onRowSettled?.(skipped, index);
      continue;
    }

    if (!row.eligible) {
      const refused: BulkInviteSendRowResult = {
        normalizedEmail: row.normalizedEmail,
        maskedAddress: row.maskedAddress,
        duplicateCount: row.duplicateCount,
        outcome: {
          status: "refused",
          reason:
            row.previewFlag === "eligible"
              ? "invalid_address"
              : (row.previewFlag as Exclude<BulkInvitePreviewFlag, "eligible">),
          sentence: row.previewSentence ?? BULK_INVITE_REFUSAL_SENTENCE.invalid_address,
        },
      };
      results.push(refused);
      options.onRowSettled?.(refused, index);
      continue;
    }

    let createOutcome;
    try {
      createOutcome = await inviteAccountMember(
        options.client,
        options.accountId,
        row.normalizedEmail,
        options.role,
        null,
        null,
        null,
      );
    } catch {
      createOutcome = { ok: false as const, reason: "Couldn't complete that invite change - please try again." };
    }

    if (!options.isCurrent()) {
      // Ambiguous: create may have succeeded; do not invent "no reservation".
      const ambiguous: BulkInviteSendRowResult = {
        normalizedEmail: row.normalizedEmail,
        maskedAddress: row.maskedAddress,
        duplicateCount: row.duplicateCount,
        outcome: {
          status: "unavailable",
          reason: "ambiguous",
          sentence:
            "Invite may already be reserved. Reconcile pending invites before sending again.",
        },
      };
      results.push(ambiguous);
      options.onRowSettled?.(ambiguous, index);
      stopNewCreates = "ambiguous";
      continue;
    }

    if (!createOutcome.ok) {
      const mapped = mapInviteRefusal(createOutcome.reason);
      const refused: BulkInviteSendRowResult = {
        normalizedEmail: row.normalizedEmail,
        maskedAddress: row.maskedAddress,
        duplicateCount: row.duplicateCount,
        outcome: mapped,
      };
      results.push(refused);
      options.onRowSettled?.(refused, index);
      continue;
    }

    const inviteId = createOutcome.inviteId;
    if (inviteId === null) {
      const ambiguous: BulkInviteSendRowResult = {
        normalizedEmail: row.normalizedEmail,
        maskedAddress: row.maskedAddress,
        duplicateCount: row.duplicateCount,
        outcome: {
          status: "unavailable",
          reason: "ambiguous",
          sentence:
            "Invite may already be reserved. Reconcile pending invites before sending again.",
        },
      };
      results.push(ambiguous);
      options.onRowSettled?.(ambiguous, index);
      stopNewCreates = "ambiguous";
      continue;
    }

    const mail = await sendMemberAuthInviteEmail(
      options.client,
      options.accountId,
      inviteId,
      options.redirectTo,
    );

    if (!options.isCurrent()) {
      const reserved: BulkInviteSendRowResult = {
        normalizedEmail: row.normalizedEmail,
        maskedAddress: row.maskedAddress,
        duplicateCount: row.duplicateCount,
        outcome: {
          status: "reserved",
          authOutcome: "unknown",
          inviteId,
          sentence: "Membership was reserved. Sign-in mail status is unknown; reconcile before retrying.",
        },
      };
      results.push(reserved);
      options.onRowSettled?.(reserved, index);
      stopNewCreates = "ambiguous";
      continue;
    }

    const mailMapped = mapMailOutcome(mail);
    const reserved: BulkInviteSendRowResult = {
      normalizedEmail: row.normalizedEmail,
      maskedAddress: row.maskedAddress,
      duplicateCount: row.duplicateCount,
      outcome: {
        status: "reserved",
        authOutcome: mailMapped.authOutcome,
        inviteId,
        sentence: mailMapped.sentence,
      },
    };
    results.push(reserved);
    options.onRowSettled?.(reserved, index);

    if (mailMapped.authOutcome === "mail_budget") {
      stopNewCreates = "auth_mail_budget";
    }
  }

  return {
    generation: options.generation,
    results,
    stoppedReason:
      stopNewCreates === "auth_mail_budget"
        ? "auth_mail_budget"
        : stopNewCreates === "ambiguous"
          ? "ambiguous"
          : stopNewCreates === "cancelled" || stopNewCreates === "stale"
            ? stopNewCreates
            : "done",
  };
}

/** Exact allowlisted Edge message used by G-INV-06 discrimination (re-export for UI copy). */
export const BULK_LOCAL_AUTH_MAIL_BUDGET_EDGE_MESSAGE = LOCAL_AUTH_MAIL_BUDGET_MESSAGE;
